import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import apiService from '../../../services/api';
import { usePageLoaderContext } from '../../../contexts/PageLoaderContext';
import { useNotification } from '../../../contexts/NotificationContext';
import CustomDropdown, { DropdownOption } from '../../UI/CustomDropdown';
import sidePanelStyles from '../../../styles/SidePanel.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import { useAuth } from '../../../contexts/AuthContext';

interface AttendanceMarks {
    subject: string;
    mid1: string;
    online1: string;
    mid2: string;
    online2: string;
}

interface AttendanceMonth {
    month: string;
    percent: string;
}

interface CounsellorComment {
    date: string;
    comment: string;
}

export interface CounsellingFormData {
    academic_year?: string;
    student_registration_number?: string;
    student_name?: string;
    year: string;
    semester: string;
    branch: string;
    hostel: string;
    transportMode: string;
    journeyTime: string;
    haveTextBooks: string;
    difficultSubjects: string;
    difficultSubjectsReason: string;
    easySubjects: string;
    easySubjectsReason: string;
    attendanceMarks: AttendanceMarks[];
    attendanceMonths: AttendanceMonth[];
    remedialSuggestions: string;
    studyHours: string;
    recordHours: string;
    entertainmentHours: string;
    counsellingSuggestions: string;
    bestFriends: string;
    parentInteraction: string;
    facultyEncouragement: string;
    disturbances: string;
    counsellorComments: CounsellorComment[];
}

const defaultValues: CounsellingFormData = {
    academic_year: '',
    student_registration_number: '',
    year: '',
    semester: '',
    branch: '',
    hostel: '',
    transportMode: '',
    journeyTime: '',
    haveTextBooks: 'No',
    difficultSubjects: '',
    difficultSubjectsReason: '',
    easySubjects: '',
    easySubjectsReason: '',
    attendanceMarks: [],
    attendanceMonths: [],
    remedialSuggestions: '',
    studyHours: '',
    recordHours: '',
    entertainmentHours: '',
    counsellingSuggestions: '',
    bestFriends: '',
    parentInteraction: '',
    facultyEncouragement: '',
    disturbances: 'No',
    counsellorComments: [],
};

interface CounsellingFormProps {
    initialData?: Partial<CounsellingFormData>;
    onSubmit: (data: CounsellingFormData) => Promise<void>;
    onCancel: () => void;
}



const CounsellingForm: React.FC<CounsellingFormProps> = ({ initialData, onSubmit, onCancel }) => {
    const { user } = useAuth();
    const { control, handleSubmit, register, formState: { errors }, reset, getValues, setValue, watch } = useForm<CounsellingFormData>({
        defaultValues: {
            ...defaultValues,
            attendanceMarks: initialData?.attendanceMarks || [],
            attendanceMonths: initialData?.attendanceMonths || [],
            counsellorComments: initialData?.counsellorComments || [],
            ...initialData
        },
    });

    const { showLoader, hideLoader } = usePageLoaderContext();
    const notification = useNotification();
    const [academicYearOptions, setAcademicYearOptions] = useState<DropdownOption[]>([]);

    // Auto-load Logic State
    const [departmentOptions, setDepartmentOptions] = useState<DropdownOption[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<string>('');
    const [studentOptions, setStudentOptions] = useState<DropdownOption[]>([]);
    const [isLoadingStudents, setIsLoadingStudents] = useState(false);

    // Initialize Department Options from User Groups
    useEffect(() => {
        if (user && user.groups && user.groups.length > 0) {
            const groups = user.groups; // string[] of group names
            const options = groups.map((g: string) => ({ value: g, label: g }));
            setDepartmentOptions(options);

            // Auto-select if only one group
            if (groups.length === 1) {
                setSelectedDepartment(groups[0]);
            }
        }
    }, [user]);

    // Fetch Students when Department Changes
    useEffect(() => {
        const fetchStudents = async () => {
            if (!selectedDepartment) {
                setStudentOptions([]);
                return;
            }

            setIsLoadingStudents(true);
            try {
                const response: any = await apiService.get(`/api/students/?department=${encodeURIComponent(selectedDepartment)}`);
                const students = Array.isArray(response) ? response : (response.results || response.data || []);

                const options = students.map((s: any) => ({
                    value: s.registration_number,
                    label: `${s.registration_number} - ${s.full_name}`
                }));
                setStudentOptions([{ value: '', label: 'Select Student' }, ...options]);
            } catch (error) {
                console.error('Error fetching students:', error);
                notification.error('Failed to load students for the selected department.');
            } finally {
                setIsLoadingStudents(false);
            }
        };

        fetchStudents();
    }, [selectedDepartment]);


    // Load academic years (existing code)
    useEffect(() => {
        const loadAcademicYears = async () => {
            try {
                const response: any = await apiService.get('/api/academic/academic-years/');
                const years = Array.isArray(response) ? response : (response.results || response.data || []);
                const options: DropdownOption[] = [
                    { value: '', label: 'Select Academic Year' },
                    ...years.map((year: any) => ({
                        value: year.id.toString(),
                        label: year.name
                    }))
                ];
                setAcademicYearOptions(options);

                // Auto-select current academic year if not editing and no initial data
                if (!initialData?.academic_year && years.length > 0) {
                    const currentYear = years.find((y: any) => y.is_current);
                    if (currentYear) {
                        reset({
                            ...getValues(),
                            academic_year: currentYear.id.toString()
                        });
                    }
                }
            } catch (err) {
                console.error('Error loading academic years:', err);
            }
        };
        loadAcademicYears();
    }, []);

    const { fields: attendanceFields, append: appendAttendance, remove: removeAttendance } = useFieldArray({
        control,
        name: 'attendanceMarks',
    });

    const { fields: monthFields, append: appendMonth, remove: removeMonth } = useFieldArray({
        control,
        name: 'attendanceMonths',
    });

    const { fields: commentFields, append: appendComment, remove: removeComment } = useFieldArray({
        control,
        name: 'counsellorComments',
    });

    React.useEffect(() => {
        if (initialData) {
            reset({
                ...defaultValues,
                attendanceMarks: initialData.attendanceMarks || [],
                attendanceMonths: initialData.attendanceMonths || [],
                counsellorComments: initialData.counsellorComments || [],
                ...initialData
            });
        }
    }, [initialData, reset]);

    const handleFormSubmit = async (data: CounsellingFormData) => {
        const isEditMode = !!initialData;
        showLoader(isEditMode ? 'Updating counselling record...' : 'Creating counselling record...');
        try {
            await onSubmit(data);
            reset(defaultValues);
        } catch (error) {
            console.error('Submission error:', error);
        } finally {
            hideLoader();
        }
    };

    // Helper for grid Layout
    const gridStyle = {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '1rem',
        marginBottom: '1rem'
    };

    // Dropdown options
    const yearOptions: DropdownOption[] = [
        { value: '', label: 'Select Year' },
        { value: 'I', label: 'I' },
        { value: 'II', label: 'II' },
        { value: 'III', label: 'III' },
        { value: 'IV', label: 'IV' },
    ];

    const semesterOptions: DropdownOption[] = [
        { value: '', label: 'Select Semester' },
        { value: 'I', label: 'I' },
        { value: 'II', label: 'II' },
    ];

    const transportModeOptions: DropdownOption[] = [
        { value: '', label: 'Select Mode' },
        { value: 'Bus', label: 'Bus' },
        { value: 'Own', label: 'Own' },
        { value: 'Walking', label: 'Walking' },
    ];

    const haveTextBooksOptions: DropdownOption[] = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];

    const monthOptions: DropdownOption[] = [
        { value: 'January', label: 'January' },
        { value: 'February', label: 'February' },
        { value: 'March', label: 'March' },
        { value: 'April', label: 'April' },
        { value: 'May', label: 'May' },
        { value: 'June', label: 'June' },
        { value: 'July', label: 'July' },
        { value: 'August', label: 'August' },
        { value: 'September', label: 'September' },
        { value: 'October', label: 'October' },
        { value: 'November', label: 'November' },
        { value: 'December', label: 'December' },
    ];

    const addSubject = () => {
        appendAttendance({ subject: '', mid1: '', online1: '', mid2: '', online2: '' });
    };

    const addAttendanceMonth = () => {
        appendMonth({ month: '', percent: '' });
    };

    const addComment = () => {
        appendComment({ date: '', comment: '' });
    };

    const handleStudentSelect = async (regNumber: string) => {
        setValue('student_registration_number', regNumber);

        if (!regNumber) return;

        try {
            // We could use the student object from the list if we stored it, 
            // but calling the specific endpoint ensures we get full details if needed.
            // Or better, just find it in the already fetched list if possible, but the list might be large?
            // Re-using the get-by-registration endpoint is safer for detail consistency.
            const student: any = await apiService.get(`/api/students/by_registration/?registration_number=${regNumber}`);
            if (student) {
                setValue('student_name', student.display_name || student.full_name || `${student.first_name} ${student.last_name}`);
                setValue('year', student.year || '');
                setValue('semester', student.semester || '');
                // Branch from Department as per requirement
                setValue('branch', student.department || student.branch || '');
                // notification.success('Student details auto-filled');
            }
        } catch (err) {
            console.error(err);
        }
    };


    // ... (rest of the component)

    return (
        <form onSubmit={handleSubmit(handleFormSubmit)} className={sidePanelStyles.form}>
            {/* Section 1: Basic Details */}
            <div className={sidePanelStyles.sectionHeader} style={{ marginTop: '0' }}>
                <div className={sidePanelStyles.sectionTitleSmall}>1. Basic Details</div>
            </div>

            {/* Department Selection (if multiple) */}
            {departmentOptions.length > 1 && (
                <div className={sidePanelStyles.formGroup}>
                    <label className={sidePanelStyles.formLabel}>
                        Select Department
                    </label>
                    <CustomDropdown
                        options={[{ value: '', label: 'Select Department' }, ...departmentOptions]}
                        value={selectedDepartment}
                        onChange={(val: any) => setSelectedDepartment(typeof val === 'string' ? val : val[0])}
                        placeholder="Select Department"
                        size="full"
                        variant="outlined"
                    />
                </div>
            )}


            {/* Hidden Academic Year - Auto-populated */}
            <input type="hidden" {...register('academic_year')} />

            {/* Student Selection - Replaces Manual Input */}
            {/* key={initialData ? 'edit' : 'create'} ensures fresh render if needed */}
            {!initialData && (
                <div className={sidePanelStyles.formGroup}>
                    <label className={sidePanelStyles.formLabel}>
                        Student <span className="required" style={{ color: 'red' }}>*</span>
                    </label>
                    {!selectedDepartment ? (
                        <div style={{ padding: '0.5rem', color: 'var(--gray)', fontStyle: 'italic', fontSize: 'var(--text-sm)' }}>
                            Please select a department (if applicable) to load students.
                        </div>
                    ) : (
                        <Controller
                            name="student_registration_number"
                            control={control}
                            rules={{ required: 'Student is required' }}
                            render={({ field }) => (
                                isLoadingStudents ? (
                                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--primary)' }}>Loading students...</div>
                                ) : (
                                    <CustomDropdown
                                        options={studentOptions}
                                        value={field.value || ''}
                                        onChange={(value) => {
                                            const val = Array.isArray(value) ? value[0] : value;
                                            field.onChange(val);
                                            handleStudentSelect(val);
                                        }}
                                        placeholder="Select Student"
                                        size="full"
                                        variant="outlined"
                                        searchable={true}
                                    />
                                )
                            )}
                        />
                    )}
                    {errors.student_registration_number && (
                        <span style={{ color: 'red', fontSize: 'var(--text-xs)' }}>{errors.student_registration_number.message as string}</span>
                    )}
                </div>
            )}


            {/* Hidden Student Name, Year, Semester - Auto-populated */}
            <input type="hidden" {...register('student_name')} />
            <input type="hidden" {...register('year')} />
            <input type="hidden" {...register('semester')} />
            <input type="hidden" {...register('branch')} />

            {/* Display Selected Student Details */}
            {watch('student_registration_number') && (
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1.5rem',
                    borderRadius: '16px',
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1.5rem'
                }}>
                    {/* AVATAR */}
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        backgroundColor: '#1e3a8a',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        fontWeight: '700',
                        flexShrink: 0
                    }}>
                        {(watch('student_name') || 'S').charAt(0).toUpperCase()}
                    </div>

                    {/* DETAILS COLUMN */}
                    <div style={{ flex: 1 }}>
                        <h4 style={{
                            margin: '0 0 4px 0',
                            fontSize: '18px',
                            fontWeight: '700',
                            color: '#1e3a8a',
                            lineHeight: '1.2'
                        }}>
                            {watch('student_name') || 'Student Name'}
                        </h4>

                        <div style={{
                            fontSize: '14px',
                            color: '#64748b',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <span>{watch('student_registration_number')}</span>
                        </div>

                        {/* PILL (Branch) */}
                        <div style={{ marginBottom: '16px' }}>
                            <span style={{
                                display: 'inline-block',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                backgroundColor: '#eff6ff',
                                color: '#2563eb',
                                fontSize: '12px',
                                fontWeight: '600',
                                letterSpacing: '0.02em'
                            }}>
                                {watch('branch') || 'Branch N/A'}
                            </span>
                        </div>

                        {/* STATS ROW (Year / Sem) */}
                        <div style={{
                            display: 'flex',
                            gap: '3rem',
                            borderTop: '1px solid #f1f5f9',
                            paddingTop: '12px',
                            marginTop: '4px'
                        }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Year {watch('year')}</span>
                                <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>Current Year</span>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>Sem {watch('semester')}</span>
                                <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>Current Semester</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div style={gridStyle}>
                <div>
                    <label className={sidePanelStyles.formLabel}>Hostel Info</label>
                    <input type="text" {...register('hostel')} className={sidePanelStyles.formControl} placeholder="Hostel Details" />
                </div>
                <div>
                    <label className={sidePanelStyles.formLabel}>Mode of Transport</label>
                    <Controller
                        name="transportMode"
                        control={control}
                        render={({ field }) => (
                            <CustomDropdown
                                options={transportModeOptions}
                                value={field.value || ''}
                                onChange={(value) => field.onChange(Array.isArray(value) ? value[0] : value)}
                                placeholder="Select Mode"
                                size="full"
                                variant="outlined"
                            />
                        )}
                    />
                </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
                <label className={sidePanelStyles.formLabel}>Time of Journey (min)</label>
                <input type="text" {...register('journeyTime')} className={sidePanelStyles.formControl} placeholder="45" />
            </div>

            <div style={gridStyle}>
                <div>
                    <label className={sidePanelStyles.formLabel}>Do you have all text books?</label>
                    <Controller
                        name="haveTextBooks"
                        control={control}
                        render={({ field }) => (
                            <CustomDropdown
                                options={haveTextBooksOptions}
                                value={field.value || 'No'}
                                onChange={(value) => field.onChange(Array.isArray(value) ? value[0] : value)}
                                placeholder="Select"
                                size="full"
                                variant="outlined"
                            />
                        )}
                    />
                </div>
            </div>

            {/* Section 2: Academic Status */}
            <div className={sidePanelStyles.sectionHeader} style={{ marginTop: '2rem' }}>
                <div className={sidePanelStyles.sectionTitleSmall}>2. Academic Status</div>
            </div>
            <div style={gridStyle}>
                <div>
                    <label className={sidePanelStyles.formLabel}>Most Difficult Subjects</label>
                    <input type="text" {...register('difficultSubjects')} className={sidePanelStyles.formControl} />
                </div>
                <div>
                    <label className={sidePanelStyles.formLabel}>Reason (Why?)</label>
                    <input type="text" {...register('difficultSubjectsReason')} className={sidePanelStyles.formControl} />
                </div>
            </div>
            <div style={gridStyle}>
                <div>
                    <label className={sidePanelStyles.formLabel}>Most Easy Subjects</label>
                    <input type="text" {...register('easySubjects')} className={sidePanelStyles.formControl} />
                </div>
                <div>
                    <label className={sidePanelStyles.formLabel}>Reason (Why?)</label>
                    <input type="text" {...register('easySubjectsReason')} className={sidePanelStyles.formControl} />
                </div>
            </div>

            {/* Section 3: Attendance & Marks */}
            <div className={sidePanelStyles.sectionHeader} style={{ marginTop: '2rem' }}>
                <div className={sidePanelStyles.sectionTitleSmall}>3. Percentage of Attendance & Internal Marks</div>
            </div>

            {/* Subjects with Marks */}
            <div className={sidePanelStyles.formGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <label className={sidePanelStyles.formLabel} style={{ marginBottom: 0 }}>Subjects & Internal Marks</label>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {attendanceFields.length === 0 ? (
                        <div style={{
                            padding: '2rem 1rem',
                            textAlign: 'center',
                            color: 'var(--gray)',
                            fontSize: 'var(--text-sm)',
                            backgroundColor: 'var(--gray-lighter)',
                            borderRadius: '8px',
                            border: '1px dashed var(--border)'
                        }}>
                            No subjects added. Click "Add Another Subject" to add one.
                        </div>
                    ) : (
                        attendanceFields.map((field, index) => (
                            <div key={field.id} style={{
                                border: '1px solid var(--border)',
                                borderRadius: '8px',
                                padding: '1rem',
                                backgroundColor: '#fff',
                                position: 'relative'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '1rem',
                                    paddingBottom: '0.5rem',
                                    borderBottom: '1px solid var(--border-light)'
                                }}>
                                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>Subject #{index + 1}</span>
                                    {attendanceFields.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeAttendance(index)}
                                            style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: '500' }}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>

                                <div className={sidePanelStyles.formGroup}>
                                    <label className={sidePanelStyles.formLabel} style={{ fontSize: 'var(--text-xs)' }}>
                                        Subject Name
                                    </label>
                                    <input
                                        {...register(`attendanceMarks.${index}.subject`)}
                                        className={sidePanelStyles.formControl}
                                        placeholder="Enter subject name"
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.8rem' }}>
                                    <div>
                                        <label className={sidePanelStyles.formLabel} style={{ fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
                                            Mid-I
                                        </label>
                                        <input
                                            type="number"
                                            {...register(`attendanceMarks.${index}.mid1`)}
                                            className={sidePanelStyles.formControl}
                                            placeholder="Marks"
                                        />
                                    </div>
                                    <div>
                                        <label className={sidePanelStyles.formLabel} style={{ fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
                                            Online-I
                                        </label>
                                        <input
                                            type="number"
                                            {...register(`attendanceMarks.${index}.online1`)}
                                            className={sidePanelStyles.formControl}
                                            placeholder="Marks"
                                        />
                                    </div>
                                    <div>
                                        <label className={sidePanelStyles.formLabel} style={{ fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
                                            Mid-II
                                        </label>
                                        <input
                                            type="number"
                                            {...register(`attendanceMarks.${index}.mid2`)}
                                            className={sidePanelStyles.formControl}
                                            placeholder="Marks"
                                        />
                                    </div>
                                    <div>
                                        <label className={sidePanelStyles.formLabel} style={{ fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
                                            Online-II
                                        </label>
                                        <input
                                            type="number"
                                            {...register(`attendanceMarks.${index}.online2`)}
                                            className={sidePanelStyles.formControl}
                                            placeholder="Marks"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    <button
                        type="button"
                        onClick={addSubject}
                        className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnSm}`}
                        style={{ alignSelf: 'flex-start' }}
                    >
                        + Add Another Subject
                    </button>
                </div>
            </div>

            {/* Monthly Attendance */}
            <div className={sidePanelStyles.formGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <label className={sidePanelStyles.formLabel} style={{ marginBottom: 0 }}>Monthly Attendance</label>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {monthFields.length === 0 ? (
                        <div style={{
                            padding: '2rem 1rem',
                            textAlign: 'center',
                            color: 'var(--gray)',
                            fontSize: 'var(--text-sm)',
                            backgroundColor: 'var(--gray-lighter)',
                            borderRadius: '8px',
                            border: '1px dashed var(--border)'
                        }}>
                            No attendance months added. Click "Add Another Month" to add one.
                        </div>
                    ) : (
                        monthFields.map((field, index) => (
                            <div key={field.id} style={{
                                border: '1px solid var(--border)',
                                borderRadius: '8px',
                                padding: '1rem',
                                backgroundColor: '#fff',
                                position: 'relative'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '1rem',
                                    paddingBottom: '0.5rem',
                                    borderBottom: '1px solid var(--border-light)'
                                }}>
                                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>Month #{index + 1}</span>
                                    {monthFields.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeMonth(index)}
                                            style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: '500' }}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div>
                                        <label className={sidePanelStyles.formLabel} style={{ fontSize: 'var(--text-xs)' }}>
                                            Month
                                        </label>
                                        <Controller
                                            name={`attendanceMonths.${index}.month`}
                                            control={control}
                                            render={({ field }) => (
                                                <CustomDropdown
                                                    options={monthOptions}
                                                    value={field.value || ''}
                                                    onChange={(value) => field.onChange(Array.isArray(value) ? value[0] : value)}
                                                    placeholder="Select Month"
                                                    size="full"
                                                    variant="outlined"
                                                />
                                            )}
                                        />
                                    </div>
                                    <div>
                                        <label className={sidePanelStyles.formLabel} style={{ fontSize: 'var(--text-xs)' }}>
                                            Percentage
                                        </label>
                                        <input
                                            type="text"
                                            {...register(`attendanceMonths.${index}.percent`)}
                                            className={sidePanelStyles.formControl}
                                            placeholder="%"
                                        />
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    <button
                        type="button"
                        onClick={addAttendanceMonth}
                        className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnSm}`}
                        style={{ alignSelf: 'flex-start' }}
                    >
                        + Add Another Month
                    </button>
                </div>
            </div>

            {/* Section 4: Remedial Suggestions */}
            <div className={sidePanelStyles.sectionHeader} style={{ marginTop: '2rem' }}>
                <div className={sidePanelStyles.sectionTitleSmall}>4. Remedial Suggestions by Counselors</div>
            </div>
            <div className={sidePanelStyles.formGroup}>
                <textarea
                    {...register('remedialSuggestions')}
                    className={sidePanelStyles.formControl}
                    rows={3}
                    placeholder="Discuss difficult subjects, etc."
                ></textarea>
            </div>

            {/* Section 5: Time Management */}
            <div className={sidePanelStyles.sectionHeader} style={{ marginTop: '2rem' }}>
                <div className={sidePanelStyles.sectionTitleSmall}>5. Time Management (Daily)</div>
            </div>
            <div style={gridStyle}>
                <div>
                    <label className={sidePanelStyles.formLabel}>Hours spent on studying</label>
                    <input type="text" {...register('studyHours')} className={sidePanelStyles.formControl} />
                </div>
                <div>
                    <label className={sidePanelStyles.formLabel}>Hours spent on Record/Assignments</label>
                    <input type="text" {...register('recordHours')} className={sidePanelStyles.formControl} />
                </div>
            </div>
            <div style={gridStyle}>
                <div>
                    <label className={sidePanelStyles.formLabel}>Hours spent on TV/Friends/Games/WhatsApp</label>
                    <input type="text" {...register('entertainmentHours')} className={sidePanelStyles.formControl} />
                </div>
            </div>

            {/* Section 6: Counselling Suggestions */}
            <div className={sidePanelStyles.sectionHeader} style={{ marginTop: '2rem' }}>
                <div className={sidePanelStyles.sectionTitleSmall}>6. Counselling Suggestions</div>
            </div>
            <div className={sidePanelStyles.formGroup}>
                <label className={sidePanelStyles.formLabel}>Suggestions (considering items 8, 9, 10)</label>
                <textarea
                    {...register('counsellingSuggestions')}
                    className={sidePanelStyles.formControl}
                    rows={2}
                ></textarea>
            </div>

            {/* Section 7: Social & Personal */}
            <div className={sidePanelStyles.sectionHeader} style={{ marginTop: '2rem' }}>
                <div className={sidePanelStyles.sectionTitleSmall}>7. Social & Personal</div>
            </div>
            <div style={gridStyle}>
                <div>
                    <label className={sidePanelStyles.formLabel}>Best friends in class</label>
                    <input type="text" {...register('bestFriends')} className={sidePanelStyles.formControl} />
                </div>
                <div>
                    <label className={sidePanelStyles.formLabel}>Any Deviations / Disturbance factors</label>
                    <input type="text" {...register('disturbances')} className={sidePanelStyles.formControl} defaultValue="No" />
                </div>
            </div>
            <div className={sidePanelStyles.formGroup}>
                <label className={sidePanelStyles.formLabel}>Encouragement from faculty/Department</label>
                <input type="text" {...register('facultyEncouragement')} className={sidePanelStyles.formControl} />
            </div>

            {/* Section 8: Counsellor Comments */}
            <div className={sidePanelStyles.sectionHeader} style={{ marginTop: '2rem' }}>
                <div className={sidePanelStyles.sectionTitleSmall}>8. Counselor Comments</div>
            </div>
            <div className={sidePanelStyles.formGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <label className={sidePanelStyles.formLabel} style={{ marginBottom: 0 }}>Comments (Date wise)</label>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {commentFields.length === 0 ? (
                        <div style={{
                            padding: '2rem 1rem',
                            textAlign: 'center',
                            color: 'var(--gray)',
                            fontSize: 'var(--text-sm)',
                            backgroundColor: 'var(--gray-lighter)',
                            borderRadius: '8px',
                            border: '1px dashed var(--border)'
                        }}>
                            No comments added. Click "Add Another Comment" to add one.
                        </div>
                    ) : (
                        commentFields.map((field, index) => (
                            <div key={field.id} style={{
                                border: '1px solid var(--border)',
                                borderRadius: '8px',
                                padding: '1rem',
                                backgroundColor: '#fff',
                                position: 'relative'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '1rem',
                                    paddingBottom: '0.5rem',
                                    borderBottom: '1px solid var(--border-light)'
                                }}>
                                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>Comment #{index + 1}</span>
                                    {commentFields.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeComment(index)}
                                            style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: '500' }}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    <div>
                                        <label className={sidePanelStyles.formLabel} style={{ fontSize: 'var(--text-xs)' }}>
                                            Date
                                        </label>
                                        <input
                                            type="date"
                                            {...register(`counsellorComments.${index}.date`)}
                                            className={sidePanelStyles.formControl}
                                        />
                                    </div>
                                    <div>
                                        <label className={sidePanelStyles.formLabel} style={{ fontSize: 'var(--text-xs)' }}>
                                            Comment
                                        </label>
                                        <textarea
                                            {...register(`counsellorComments.${index}.comment`)}
                                            className={sidePanelStyles.formControl}
                                            rows={3}
                                            placeholder="Enter comment..."
                                        ></textarea>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    <button
                        type="button"
                        onClick={addComment}
                        className={`${buttonStyles.btn} ${buttonStyles.btnSecondary} ${buttonStyles.btnSm}`}
                        style={{ alignSelf: 'flex-start' }}
                    >
                        + Add Another Comment
                    </button>
                </div>
            </div>

            <div className={sidePanelStyles.formActions}>
                <button type="button" className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`} onClick={onCancel}>
                    Cancel
                </button>
                <button type="submit" className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}>
                    {initialData ? 'Update Form' : 'Submit Form'}
                </button>
            </div>
        </form>
    );
};

export default CounsellingForm;
